/**
 * web/screens/worldMap.ts
 * World map — five world cards + stage shortcut rows for unlocked worlds.
 *
 * Lock state is re-derived on every render via
 * {@link isWorldUnlocked} + {@link isSecretStageUnlocked}, both of which
 * read the SaveData snapshot loaded once on mount. Subscribes to user
 * settings so flipping `devUnlockWorld2` / `devUnlockWorld3` /
 * `devUnlockWorld4` in Settings re-renders the locked label.
 *
 * @module web/screens/worldMap
 */

/// <reference lib="dom" />

import type { Route, Router } from '../router';
import { injectStyles } from '../styles';
import * as SaveManager from '@src/game/services/saveManager';
import {
  countCollectedShards,
  countCompletedMainLevels,
  MAIN_LEVELS_PER_WORLD,
  SHARDS_PER_WORLD,
} from '@src/game/data/worldProgress';
import { isSecretStageUnlocked, isWorldUnlocked } from '@src/game/data/worldUnlock';
import { getUserSetting, subscribeUserSettings } from '@src/game/services/userSettings';
import type { SaveData } from '@src/game/types/save';
import { STAGES_BY_WORLD, WORLDS, type WorldEntry } from './worldMapData';

/**
 * Build a unicode star row that visualises stages cleared.
 *
 * @param cleared - Number of main stages completed (0..total).
 * @param total - Total main stages in this world.
 * @returns A run of filled (`★`) + empty (`☆`) star chars.
 */
function buildStarsLabel(cleared: number, total: number): string {
  const clamped = Math.max(0, Math.min(cleared, total));
  return '★'.repeat(clamped) + '☆'.repeat(total - clamped);
}

function buildWorldCard(
  world: WorldEntry,
  save: SaveData | null,
  devUnlockWorld2: boolean,
  devUnlockWorld3: boolean,
  devUnlockWorld4: boolean,
  router: Router,
): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'sf-world-cell';
  const locked = !isWorldUnlocked(save, world.id, {
    devUnlockWorld2,
    devUnlockWorld3,
    devUnlockWorld4,
  });
  // DECISION: A world is `playable` only when the unlock gate is open
  // AND a route is wired. Every shipped world now has a non-null route,
  // but `WorldEntry.route` still permits `null` so a future world can be
  // gate-open yet route-unwired during its bootstrap. When that happens
  // the card gets the same muted styling as a locked card and surfaces a
  // distinct "COMING SOON" label below — keeping any dev override
  // observable (LOCKED → COMING SOON transition) without ever rendering a
  // click affordance that does nothing.
  const playable = !locked && world.route !== null;

  const card = document.createElement('div');
  card.className = playable ? 'sf-card' : 'sf-card sf-card-locked';
  card.dataset['worldId'] = String(world.id);
  if (playable) card.style.borderColor = world.accent;

  // Accent strip header — purely decorative, gives each world card a
  // distinct band of its constellation colour at the top.
  const accentStrip = document.createElement('div');
  accentStrip.className = 'sf-card-accent-strip';
  accentStrip.style.background = world.accent;

  const num = document.createElement('p');
  num.className = 'sf-card-subtitle';
  num.textContent = `WORLD ${world.id}`;

  const name = document.createElement('h3');
  name.className = 'sf-card-title';
  name.textContent = world.name;
  if (playable) name.style.color = world.accent;

  const constellation = document.createElement('p');
  constellation.className = 'sf-card-meta';
  constellation.textContent = world.constellation;

  // Visual shard progress bar: dim track + accent-coloured fill.
  //
  // DECISION: Clamp for DISPLAY here rather than inside
  // `countCollectedShards`. That function's contract is to count, and
  // `isSecretStageUnlocked` compares its raw result against
  // `SHARDS_PER_WORLD` — clamping at the source would hide a corrupt save
  // from the gate as well as from the label. The bar was already clamped;
  // the label was not, so a hand-edited save with extra shard IDs in one
  // level rendered the nonsense "✨ 20 / 12 shards" beside a full bar.
  // Both now agree.
  const rawShardCount = countCollectedShards(save, world.id);
  const shardCount = Math.max(0, Math.min(SHARDS_PER_WORLD, rawShardCount));
  const shardLabel = document.createElement('p');
  shardLabel.className = 'sf-card-meta sf-card-shard-label';
  shardLabel.textContent = `✨ ${shardCount} / ${SHARDS_PER_WORLD} shards`;
  const shardTrack = document.createElement('div');
  shardTrack.className = 'sf-card-shard-track';
  const shardFill = document.createElement('div');
  shardFill.className = 'sf-card-shard-fill';
  const shardPct = shardCount / SHARDS_PER_WORLD;
  shardFill.style.width = `${(shardPct * 100).toFixed(1)}%`;
  shardFill.style.background = world.accent;
  shardTrack.append(shardFill);

  card.append(accentStrip, num, name, constellation, shardLabel, shardTrack);

  if (!playable) {
    const statusTag = document.createElement('p');
    statusTag.className = 'sf-card-meta';
    statusTag.style.color = 'var(--color-muted)';
    // "COMING SOON" surfaces when the unlock gate is open but no route
    // is wired yet — a defensive state for a future world mid-bootstrap
    // (no shipped world is in it today). Keeps the dev-override visible
    // without pretending the card is interactive.
    statusTag.textContent = locked ? 'LOCKED' : 'COMING SOON';
    card.append(statusTag);
  } else {
    // Star-row visualisation of main stages cleared (★★★☆ style).
    // DECISION: clamp once here and feed the same value to both the
    // star string and the aria-label so they can't disagree. The
    // data layer (countCompletedMainLevels) already caps the count
    // via parseMainStageNumber, but anchoring both views on one
    // local value keeps the invariant visible at the use site.
    const cleared = Math.min(
      countCompletedMainLevels(save, world.id),
      MAIN_LEVELS_PER_WORLD,
    );
    const stars = document.createElement('p');
    stars.className = 'sf-card-meta sf-card-stage-stars';
    stars.style.color = world.accent;
    stars.textContent = buildStarsLabel(cleared, MAIN_LEVELS_PER_WORLD);
    // role="img" promotes the run of unicode stars (a graphic, not
    // semantic text) into a labelled image so screen readers actually
    // announce the aria-label. Without a role, the aria-label on a
    // bare <p> is unreliable across assistive tech.
    stars.setAttribute('role', 'img');
    stars.setAttribute(
      'aria-label',
      `${cleared} of ${MAIN_LEVELS_PER_WORLD} stages cleared`,
    );
    card.append(stars);
  }

  if (playable) {
    // DECISION: Stage buttons are siblings of the card (appended to `wrap`
    // below), not descendants — so any click on the card is a card click.
    // Earlier code guarded with `.closest('.sf-stage-btn')` which could never
    // match and also assumed e.target was always an Element.
    // Pass the first stage's level id so cross-world dispatchers (game.ts
    // → platformerSessionBundle) load the correct world; otherwise an
    // empty `?level=` falls back to the default world (W1).
    const firstStage = STAGES_BY_WORLD[world.id]?.find((s) => s.kind === 'main');
    card.addEventListener('click', () => {
      if (firstStage) {
        router.navigate(world.route!, { level: firstStage.level });
      } else {
        router.navigate(world.route!);
      }
    });
  }

  wrap.append(card);

  if (playable) {
    const stages = STAGES_BY_WORLD[world.id];
    if (stages && stages.length > 0) {
      const secretUnlocked = isSecretStageUnlocked(save, world.id);
      const row = document.createElement('div');
      row.className = 'sf-stage-row';
      for (const stage of stages) {
        if (stage.kind === 'secret' && !secretUnlocked) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sf-stage-btn';
        btn.textContent = `Stage ${stage.label}`;
        btn.addEventListener('click', () => {
          router.navigate(world.route!, { level: stage.level });
        });
        row.append(btn);
      }
      wrap.append(row);
    }
  }

  return wrap;
}

/** World-map route factory. */
export const worldMapScreen: Route = (router) => {
  injectStyles();
  const screen = document.createElement('section');
  screen.className = 'sf-screen';
  screen.dataset['screen'] = 'world-map';

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'sf-btn sf-btn-back';
  back.textContent = '← Back';
  back.addEventListener('click', () => router.back('/'));

  const heading = document.createElement('h1');
  heading.className = 'sf-screen-title';
  heading.textContent = 'SELECT WORLD';

  const list = document.createElement('div');
  list.className = 'sf-card-grid';

  screen.append(back, heading, list);
  const root = document.getElementById('app');
  root?.append(screen);

  let save: SaveData | null = null;
  let devUnlockWorld2 = getUserSetting('devUnlockWorld2');
  let devUnlockWorld3 = getUserSetting('devUnlockWorld3');
  let devUnlockWorld4 = getUserSetting('devUnlockWorld4');
  let destroyed = false;

  const render = (): void => {
    list.innerHTML = '';
    for (const world of WORLDS) {
      list.append(
        buildWorldCard(world, save, devUnlockWorld2, devUnlockWorld3, devUnlockWorld4, router),
      );
    }
  };

  render();

  // Async save load — re-render when it lands.
  void SaveManager.load().then((data) => {
    if (destroyed) return;
    save = data;
    render();
  });

  const unsubscribe = subscribeUserSettings(() => {
    const nextW2 = getUserSetting('devUnlockWorld2');
    const nextW3 = getUserSetting('devUnlockWorld3');
    const nextW4 = getUserSetting('devUnlockWorld4');
    if (
      nextW2 !== devUnlockWorld2 ||
      nextW3 !== devUnlockWorld3 ||
      nextW4 !== devUnlockWorld4
    ) {
      devUnlockWorld2 = nextW2;
      devUnlockWorld3 = nextW3;
      devUnlockWorld4 = nextW4;
      render();
    }
  });

  return {
    destroy: () => {
      destroyed = true;
      unsubscribe();
      screen.remove();
    },
  };
};
